"use client";

import { useState, useEffect, useMemo } from 'react';
import Navigation from '@/components/Navigation';
import { catalogApi, Category, ProductTemplate } from '@/lib/api/catalog';
import { salesApi, Cart } from '@/lib/api/sales';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { fetchCarMakes } from '@/lib/car-makes';
import { fetchCarModels } from '@/lib/car-taxonomy';
import type { CarMake, CarModel } from '@/app/lib/mock-db';
import {
  Search,
  ShoppingCart,
  Banknote,
  CreditCard,
  Heart,
  Landmark,
  Trash2,
  CheckCircle2,
  PackageCheck,
  Loader2,
  ArrowRight,
  Grid3X3,
  SlidersHorizontal,
  X,
  Wrench,
  Disc3,
  BatteryCharging,
  CircleGauge,
  Lightbulb,
  Cable,
  Cog,
  Droplet,
  Gauge,
  ShieldCheck,
  Plus,
  type LucideIcon,
} from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useCountry } from '@/context/CountryContext';

const PART_CATEGORY_IMAGES = [
  {
    keys: ['engine', 'motor', 'محرك', 'المحرك', 'زيت', 'زيوت', 'filter', 'فلتر'],
    image: 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?q=80&w=900&auto=format&fit=crop',
    accent: 'from-blue-600/85 to-slate-950/80',
    Icon: Cog,
  },
  {
    keys: ['brake', 'فرامل', 'بريك', 'فحمات'],
    image: 'https://images.unsplash.com/photo-1625047509248-ec889cbff17f?q=80&w=900&auto=format&fit=crop',
    accent: 'from-red-600/85 to-slate-950/80',
    Icon: Disc3,
  },
  {
    keys: ['battery', 'electric', 'كهرباء', 'بطارية'],
    image: 'https://images.unsplash.com/photo-1621905251918-48416bd8575a?q=80&w=900&auto=format&fit=crop',
    accent: 'from-indigo-600/85 to-slate-950/80',
    Icon: BatteryCharging,
  },
  {
    keys: ['tire', 'wheel', 'إطارات', 'كفرات', 'جنط'],
    image: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?q=80&w=900&auto=format&fit=crop',
    accent: 'from-slate-700/85 to-slate-950/80',
    Icon: CircleGauge,
  },
  {
    keys: ['light', 'lamp', 'أنوار', 'إضاءة'],
    image: 'https://images.unsplash.com/photo-1542362567-b07e54358753?q=80&w=900&auto=format&fit=crop',
    accent: 'from-orange-500/85 to-slate-950/80',
    Icon: Lightbulb,
  },
  {
    keys: ['suspension', 'مساعد', 'مساعدات'],
    image: 'https://images.unsplash.com/photo-1517524008697-84bbe3c3fd98?q=80&w=900&auto=format&fit=crop',
    accent: 'from-emerald-600/85 to-slate-950/80',
    Icon: Gauge,
  },
];

const PART_FALLBACK_ICONS: LucideIcon[] = [
  Wrench,
  PackageCheck,
  Cable,
  Cog,
  Droplet,
  ShieldCheck,
  BatteryCharging,
  Lightbulb,
];

const PART_FALLBACK_IMAGES = [
  'https://images.unsplash.com/photo-1609521263047-f8f205293f24?q=80&w=900&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1635773054018-20835064619a?q=80&w=900&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1527383418406-f85a3b146499?q=80&w=900&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1565043589221-1a6fd9ae45c7?q=80&w=900&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1606577924006-27d39b132ae2?q=80&w=900&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1563720223185-11003d516935?q=80&w=900&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1619642751034-765dfdf7c58e?q=80&w=900&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1580273916550-e323be2ae537?q=80&w=900&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1605559424843-9e4c228bf1c2?q=80&w=900&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1615906655593-ad0386982a0f?q=80&w=900&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1597404294360-feeeda04612e?q=80&w=900&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1502161254066-6c74afbf07aa?q=80&w=900&auto=format&fit=crop',
];

type CheckoutPaymentMethod = 'cod' | 'card' | 'bank';

const CHECKOUT_PAYMENT_METHODS: Array<{
  value: CheckoutPaymentMethod;
  label: string;
  hint: string;
  Icon: LucideIcon;
}> = [
  { value: 'cod', label: 'الدفع عند الاستلام', hint: 'يتم الدفع عند تسليم القطعة', Icon: Banknote },
  { value: 'card', label: 'بطاقة', hint: 'محجوز كدفع معلق حتى ربط البوابة', Icon: CreditCard },
  { value: 'bank', label: 'تحويل بنكي', hint: 'يسجل الطلب بانتظار مراجعة التحويل', Icon: Landmark },
];

function normalizePaymentCode(code: string): CheckoutPaymentMethod | null {
  const normalized = code.trim().toLowerCase();
  if (['cash', 'cod', 'cash_on_delivery'].includes(normalized)) return 'cod';
  if (normalized === 'card') return 'card';
  if (['bank', 'transfer', 'bank_transfer'].includes(normalized)) return 'bank';
  return null;
}

function checkoutErrorMessage(error: any, fallback: string) {
  const payload = error?.response?.data || error;
  const raw = payload?.message || payload?.error || payload?.detail || payload?.errors || '';
  const text = typeof raw === 'string' ? raw : JSON.stringify(raw);
  if (text.includes('token_not_valid')) return 'انتهت الجلسة. يرجى تسجيل الدخول مرة أخرى ثم إعادة المحاولة.';
  if (text.includes('not enabled')) return 'طريقة الدفع غير مفعلة لهذه الدولة من لوحة الإدارة.';
  if (text.includes('Coupon') || text.includes('coupon')) return 'القسيمة غير صالحة أو لا تنطبق على هذا الطلب.';
  return text && text !== '{}' ? text.replace(/ErrorDetail|string|code=/g, '').slice(0, 160) : fallback;
}

function normalizeCategoryText(value: unknown): string {
  return String(value || '').trim().toLowerCase();
}

function getPartCategoryVisual(category?: Category | null, index = 0) {
  const text = normalizeCategoryText(`${category?.name || ''} ${category?.name_ar || ''} ${category?.slug || ''}`);
  const matched = PART_CATEGORY_IMAGES.find((entry) => entry.keys.some((key) => text.includes(key)));
  if (matched) return matched;

  return {
    image: PART_FALLBACK_IMAGES[index % PART_FALLBACK_IMAGES.length],
    accent: 'from-primary/85 to-slate-950/80',
    Icon: PART_FALLBACK_ICONS[index % PART_FALLBACK_ICONS.length],
  };
}

export default function PartsStore() {
  const { toast } = useToast();
  const { selectedCountry, selectedCountryCode } = useCountry();
  const currencyLabel = selectedCountry?.currency || selectedCountry?.currency_symbol || (selectedCountryCode === 'OM' ? 'ر.ع' : 'ر.س');
  const cityPlaceholder = selectedCountryCode === 'OM' ? 'مسقط، صحار، صلالة...' : 'الرياض، جدة، الدمام...';

  // Data
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<ProductTemplate[]>([]);
  const [cart, setCart] = useState<Cart | null>(null);
  const [carMakes, setCarMakes] = useState<CarMake[]>([]);
  const [carModels, setCarModels] = useState<CarModel[]>([]);

  // State
  const [loading, setLoading] = useState(true);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [orderCompleted, setOrderCompleted] = useState(false);
  const [completedOrder, setCompletedOrder] = useState<any | null>(null);
  const [submittingOrder, setSubmittingOrder] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponChecking, setCouponChecking] = useState(false);
  const [couponPreview, setCouponPreview] = useState<any | null>(null);
  const [couponError, setCouponError] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<CheckoutPaymentMethod>('cod');
  const [completedPaymentMethod, setCompletedPaymentMethod] = useState<CheckoutPaymentMethod>('cod');
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [selectedCategoryObject, setSelectedCategoryObject] = useState<Category | null>(null);
  const [browseStep, setBrowseStep] = useState<'categories' | 'items'>('categories');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMakeId, setSelectedMakeId] = useState('__all');
  const [selectedModelId, setSelectedModelId] = useState('__all');
  const [selectedYear, setSelectedYear] = useState('__all');
  const [availabilityFilter, setAvailabilityFilter] = useState<'all' | 'in_stock'>('all');
  const [sortOption, setSortOption] = useState<'relevance' | 'price_asc' | 'price_desc' | 'name'>('relevance');

  const [address, setAddress] = useState({
    name: '',
    phone: '',
    city: '',
    fullAddress: ''
  });

  useEffect(() => {
    fetchData();
    fetchCart();
  }, [selectedCountryCode]);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search).get('search');
    if (query?.trim()) {
      setSearchQuery(query.trim());
      setBrowseStep('items');
    }
  }, []);

  const filteredCarModels = useMemo(() => {
    if (selectedMakeId === '__all') return carModels;
    return carModels.filter((model) => model.make_id === selectedMakeId);
  }, [carModels, selectedMakeId]);

  const enabledPaymentMethods = useMemo(() => {
    const codes = selectedCountry?.payment_method_codes || [];
    const normalized = codes.map(normalizePaymentCode).filter(Boolean) as CheckoutPaymentMethod[];
    const unique = Array.from(new Set(normalized));
    return unique.length > 0
      ? CHECKOUT_PAYMENT_METHODS.filter((method) => unique.includes(method.value))
      : CHECKOUT_PAYMENT_METHODS;
  }, [selectedCountry?.payment_method_codes]);

  useEffect(() => {
    if (!enabledPaymentMethods.some((method) => method.value === paymentMethod)) {
      setPaymentMethod(enabledPaymentMethods[0]?.value || 'cod');
    }
  }, [enabledPaymentMethods, paymentMethod]);

  const selectedMake = useMemo(
    () => carMakes.find((make) => make.id === selectedMakeId) || null,
    [carMakes, selectedMakeId]
  );

  const selectedModel = useMemo(
    () => carModels.find((model) => model.id === selectedModelId) || null,
    [carModels, selectedModelId]
  );

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 18 }, (_, index) => String(currentYear - index));
  }, []);

  const hasVehicleFilters =
    selectedMakeId !== '__all' ||
    selectedModelId !== '__all' ||
    selectedYear !== '__all' ||
    availabilityFilter !== 'all' ||
    sortOption !== 'relevance';

  const couponDiscount = Math.max(0, Number(couponPreview?.discount_amount || 0));
  const checkoutSubtotal = Number(cart?.total || 0);
  const checkoutTotal = Math.max(0, checkoutSubtotal - couponDiscount);

  useEffect(() => {
    fetchProducts();
  }, [selectedCategory, searchQuery, selectedMakeId, selectedModelId, selectedYear, availabilityFilter, sortOption, selectedCountryCode]);

  const fetchData = async () => {
    try {
      const [cats, makes, models] = await Promise.all([
        catalogApi.getCategories(),
        fetchCarMakes(),
        fetchCarModels(),
      ]);
      setCategories(cats);
      setCarMakes(makes);
      setCarModels(models);
    } catch {
      console.warn("Catalog API unavailable; using local category data.");
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {
        store_type: 'parts_store,workshop',
        country_code: selectedCountryCode,
      };
      if (selectedCategory) params.category = selectedCategory.toString();
      const fitmentTerms = [
        searchQuery.trim(),
        selectedMake?.name_ar,
        selectedMake?.name_en,
        selectedModel?.name_ar,
        selectedModel?.name_en,
        selectedYear !== '__all' ? selectedYear : '',
      ].filter(Boolean);
      if (fitmentTerms.length > 0) params.search = fitmentTerms.join(' ');
      if (selectedMakeId !== '__all') params.make = selectedMakeId;
      if (selectedModelId !== '__all') params.model = selectedModelId;
      if (selectedYear !== '__all') params.year = selectedYear;

      const res = await catalogApi.getProducts(params);
      let rows = res.results || [];
      if (availabilityFilter === 'in_stock') {
        rows = rows.filter((product) => product.variants?.some((variant) => variant.is_active !== false));
      }
      rows = [...rows].sort((a, b) => {
        const priceOf = (product: ProductTemplate) => Number(product.variants?.[0]?.effective_price || product.base_price || 0);
        if (sortOption === 'price_asc') return priceOf(a) - priceOf(b);
        if (sortOption === 'price_desc') return priceOf(b) - priceOf(a);
        if (sortOption === 'name') return (a.name_ar || a.name).localeCompare(b.name_ar || b.name, 'ar');
        return 0;
      });
      setProducts(rows);
    } catch {
      console.warn("Catalog API unavailable; using local product data.");
    } finally {
      setLoading(false);
    }
  };

  const handleCategorySelect = (category: Category) => {
    setSelectedCategory(category.id);
    setSelectedCategoryObject(category);
    setBrowseStep('items');
  };

  const backToCategories = () => {
    setSelectedCategory(null);
    setSelectedCategoryObject(null);
    setSearchQuery('');
    setBrowseStep('categories');
  };

  const clearVehicleFilters = () => {
    setSelectedMakeId('__all');
    setSelectedModelId('__all');
    setSelectedYear('__all');
    setAvailabilityFilter('all');
    setSortOption('relevance');
  };

  const fetchCart = async () => {
    try {
      const c = await salesApi.getCart();
      setCart(c);
    } catch {
      console.warn("Cart API unavailable; checkout will stay empty until the backend is running.");
    }
  };

  const addToCart = async (product: ProductTemplate) => {
    if (!product.variants || product.variants.length === 0) {
      toast({ variant: "destructive", title: "لا يمكن الإضافة", description: "هذا المنتج غير متوفر حالياً" });
      return;
    }

    // Add default variant
    const variantId = product.variants[0].id;
    try {
      const updatedCart = await salesApi.addToCart(variantId, 1);
      setCart(updatedCart);
      toast({ title: "تمت الإضافة", description: `تمت إضافة ${product.name_ar} إلى السلة` });
    } catch (e) {
      toast({ variant: "destructive", title: "خطأ", description: "حدث خطأ أثناء الإضافة للسلة" });
    }
  };

  const removeFromCart = async (lineId: number) => {
    try {
      const updatedCart = await salesApi.removeFromCart(lineId);
      setCart(updatedCart);
    } catch (e) {
      toast({ variant: "destructive", title: "خطأ", description: "تعذر إزالة المنتج" });
    }
  };

  const handleValidateCoupon = async () => {
    const code = couponCode.trim();
    if (!code || !cart) {
      setCouponPreview(null);
      setCouponError('');
      return;
    }

    setCouponChecking(true);
    setCouponError('');
    try {
      const result = await salesApi.validateCheckoutCoupon({
        code,
        subtotal: cart.total,
        country_code: selectedCountryCode,
        city: address.city,
      });
      setCouponPreview(result);
      toast({
        title: 'تم قبول القسيمة',
        description: `سيتم خصم ${result.discount_amount} من الطلب عند التأكيد.`,
      });
    } catch (e: any) {
      setCouponPreview(null);
      setCouponError(checkoutErrorMessage(e, 'القسيمة غير صالحة لهذا الطلب.'));
    } finally {
      setCouponChecking(false);
    }
  };

  const handlePlaceOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!address.name || !address.phone || !address.city) {
      toast({ variant: "destructive", title: "بيانات ناقصة", description: "يرجى إكمال بيانات العنوان والشحن" });
      return;
    }

    setSubmittingOrder(true);
    try {
      const order = await salesApi.checkout({
        customer_name: address.name,
        customer_phone: address.phone,
        shipping_city: address.city,
        shipping_address: address.fullAddress,
        shipping_country: selectedCountryCode,
        payment_method: paymentMethod,
        coupon_code: couponCode.trim() || undefined,
      });

      setCompletedOrder(order);
      setCompletedPaymentMethod(paymentMethod);
      setOrderCompleted(true);
      setCouponCode('');
      setCouponPreview(null);
      setCouponError('');
      fetchCart(); // Clear cart in UI
      toast({
        title: "تم تأكيد الطلب",
        description: order?.loyalty_points_earned
          ? `تم تطبيق الطلب وكسبت ${order.loyalty_points_earned} نقطة.`
          : "شكراً لثقتك! سيتم شحن طلبك قريباً.",
      });
    } catch (e: any) {
      toast({ variant: "destructive", title: "فشل إتمام الطلب", description: checkoutErrorMessage(e, "حدث خطأ غير متوقع") });
    } finally {
      setSubmittingOrder(false);
    }
  };

  if (orderCompleted) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation title="طلب مكتمل" />
        <div className="flex flex-col items-center justify-center p-8 mt-20 text-center space-y-6">
          <div className="bg-green-100 p-6 rounded-full text-green-600 animate-bounce">
            <CheckCircle2 className="h-16 w-16" />
          </div>
          <h2 className="text-3xl font-bold font-headline">تم استلام طلبك بنجاح!</h2>
          <p className="text-muted-foreground max-w-md">
            سيقوم فريقنا بتجهيز الطلب وشحنه إلى عنوانك في {address.city}. شكراً لتسوقك معنا!
          </p>
          {completedOrder?.loyalty_points_earned > 0 && (
            <div className="rounded-2xl bg-amber-50 border border-amber-100 px-5 py-3 text-amber-800 font-bold">
              ربحت {completedOrder.loyalty_points_earned} نقطة في مكافآت مكنة
            </div>
          )}
          {completedOrder?.discount_amount && parseFloat(completedOrder.discount_amount) > 0 && (
            <div className="rounded-2xl bg-emerald-50 border border-emerald-100 px-5 py-3 text-emerald-700 font-bold">
              تم تطبيق خصم {parseFloat(completedOrder.discount_amount).toFixed(2)}
              {' '}
              {currencyLabel}
            </div>
          )}
          {completedPaymentMethod !== 'cod' && (
            <div className="rounded-2xl bg-blue-50 border border-blue-100 px-5 py-3 text-blue-700 font-bold">
              تم تسجيل طريقة الدفع كطلب معلق حتى يتم ربط بوابة الدفع أو مراجعة التحويل.
            </div>
          )}
          <button onClick={() => setOrderCompleted(false)} className="bg-primary text-white rounded-2xl px-10 h-14 text-lg font-bold">
            العودة للمتجر
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative">
      <Navigation title="متجر قطع الغيار" />

      {/* Floating Registration Button */}
      <div className="fixed bottom-6 left-6 z-50">
          <Link href="/register">
              <Button className="rounded-full h-14 px-6 gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold shadow-xl shadow-blue-600/20 transition-transform hover:scale-105 active:scale-95">
                  <Plus className="h-4 w-4 text-orange-500" />
                  تسجيل معرض سيارات
              </Button>
          </Link>
      </div>

      <div className="p-4 space-y-6 max-w-5xl mx-auto pb-24">
        <div className="flex flex-col gap-4">
          <div className="relative group">
            <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground h-5 w-5 transition-colors group-focus-within:text-primary" />
            <Input
              className="pr-12 rounded-2xl bg-white border-none shadow-sm h-14 text-lg"
              placeholder="ابحث عن قطعة (بريمبو، دينسو، فحمات...)"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                if (e.target.value.trim()) setBrowseStep('items');
              }}
            />
          </div>

          <div className="rounded-[1.5rem] bg-white p-3 shadow-sm">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm font-black text-slate-700">
                <SlidersHorizontal className="h-4 w-4 text-primary" />
                فلترة حسب السيارة
              </div>
              {hasVehicleFilters && (
                <Button variant="ghost" size="sm" onClick={clearVehicleFilters} className="h-8 rounded-full px-3 text-xs font-bold text-slate-500 hover:text-red-500">
                  <X className="ml-1 h-3.5 w-3.5" />
                  مسح
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-5">
              <Select
                value={selectedMakeId}
                onValueChange={(value) => {
                  setSelectedMakeId(value);
                  setSelectedModelId('__all');
                  setBrowseStep('items');
                }}
              >
                <SelectTrigger className="h-11 rounded-xl border-slate-100 bg-slate-50 text-sm font-bold">
                  <SelectValue placeholder="الماركة" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">كل الماركات</SelectItem>
                  {carMakes.map((make) => (
                    <SelectItem key={make.id} value={make.id}>{make.name_ar}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={selectedModelId}
                onValueChange={(value) => {
                  setSelectedModelId(value);
                  setBrowseStep('items');
                }}
              >
                <SelectTrigger className="h-11 rounded-xl border-slate-100 bg-slate-50 text-sm font-bold">
                  <SelectValue placeholder="الموديل" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">كل الموديلات</SelectItem>
                  {filteredCarModels.map((model) => (
                    <SelectItem key={model.id} value={model.id}>{model.name_ar}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={selectedYear}
                onValueChange={(value) => {
                  setSelectedYear(value);
                  setBrowseStep('items');
                }}
              >
                <SelectTrigger className="h-11 rounded-xl border-slate-100 bg-slate-50 text-sm font-bold">
                  <SelectValue placeholder="السنة" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">كل السنوات</SelectItem>
                  {yearOptions.map((year) => (
                    <SelectItem key={year} value={year}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={availabilityFilter}
                onValueChange={(value: 'all' | 'in_stock') => {
                  setAvailabilityFilter(value);
                  setBrowseStep('items');
                }}
              >
                <SelectTrigger className="h-11 rounded-xl border-slate-100 bg-slate-50 text-sm font-bold">
                  <SelectValue placeholder="التوفر" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">كل القطع</SelectItem>
                  <SelectItem value="in_stock">المتوفر فقط</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={sortOption}
                onValueChange={(value: 'relevance' | 'price_asc' | 'price_desc' | 'name') => {
                  setSortOption(value);
                  setBrowseStep('items');
                }}
              >
                <SelectTrigger className="h-11 rounded-xl border-slate-100 bg-slate-50 text-sm font-bold">
                  <SelectValue placeholder="الترتيب" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="relevance">الأكثر صلة</SelectItem>
                  <SelectItem value="price_asc">السعر: الأقل أولاً</SelectItem>
                  <SelectItem value="price_desc">السعر: الأعلى أولاً</SelectItem>
                  <SelectItem value="name">الاسم</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {browseStep === 'items' && (
            <div className="space-y-3 rounded-[1.5rem] bg-white p-3 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <Badge className="rounded-full bg-primary px-4 py-2 text-white">
                    {selectedCategoryObject?.name_ar || (searchQuery ? 'نتائج البحث' : 'كل القطع')}
                  </Badge>
                </div>
                <Button variant="outline" className="rounded-2xl shrink-0 gap-2" onClick={backToCategories}>
                  <ArrowRight className="h-4 w-4" />
                  التصنيفات
                </Button>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1">
                <button
                  onClick={() => {
                    setSelectedCategory(null);
                    setSelectedCategoryObject(null);
                    setBrowseStep('items');
                  }}
                  className={`shrink-0 rounded-full border px-4 py-2 text-xs font-bold transition-colors ${
                    !selectedCategory ? 'border-primary bg-primary text-white' : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-primary hover:text-primary'
                  }`}
                >
                  كل القطع
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategorySelect(cat)}
                    className={`shrink-0 rounded-full border px-4 py-2 text-xs font-bold transition-colors ${
                      selectedCategory === cat.id ? 'border-primary bg-primary text-white' : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-primary hover:text-primary'
                    }`}
                  >
                    {cat.name_ar}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {browseStep === 'categories' ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-black text-slate-800 flex items-center gap-2">
                <Grid3X3 className="h-6 w-6 text-primary" />
                تصفح حسب التصنيف
              </h2>
            </div>
            {categories.length === 0 ? (
              <div className="flex justify-center p-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {categories.map((cat, index) => {
                  const visual = getPartCategoryVisual(cat, index);
                  const CategoryIcon = visual.Icon;
                  return (
                    <button key={cat.id} onClick={() => handleCategorySelect(cat)} className="group text-right">
                      <div className="relative h-36 overflow-hidden rounded-[1.5rem] bg-white shadow-sm ring-1 ring-slate-100 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:shadow-xl">
                        <img src={visual.image} alt={cat.name_ar} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-slate-950/80 via-slate-950/15 to-transparent" />
                        <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-white/20 to-transparent" />
                        <div className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-2xl bg-white/95 text-primary shadow-sm backdrop-blur">
                          <CategoryIcon className="h-4 w-4" />
                        </div>
                        <div className="absolute inset-x-0 bottom-0 p-3 text-white">
                          <div className="mb-2 inline-flex h-7 w-7 items-center justify-center rounded-xl bg-white/20 text-white shadow-sm backdrop-blur-md ring-1 ring-white/25">
                            <CategoryIcon className="h-3.5 w-3.5" />
                          </div>
                          <h3 className="truncate text-sm font-black sm:text-base">{cat.name_ar}</h3>
                          <p className="mt-0.5 truncate text-[11px] font-semibold text-white/75">{cat.name}</p>
                        </div>
                      </div>
                      <div className="mt-2 flex items-center justify-between rounded-full bg-slate-50 px-3 py-1.5 text-[11px] font-bold text-slate-500">
                        <span>عرض القطع</span>
                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-primary shadow-sm">
                          <ArrowRight className="h-3.5 w-3.5" />
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ) : loading ? (
          <div className="flex justify-center p-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : products.length === 0 ? (
          <div className="text-center p-12 text-slate-500">لا توجد منتجات تطابق بحثك.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {products.map((product) => (
              <Card key={product.id} className="border-none shadow-sm hover:shadow-xl transition-all duration-300 rounded-3xl overflow-hidden group bg-white flex flex-col">
                <Link href={`/parts/${product.id}`} className="block relative aspect-square bg-slate-100 flex items-center justify-center p-4">
                  {product.images?.[0] ? (
                    <Image
                      src={product.images[0].image}
                      alt={product.name_ar}
                      fill
                      className="object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  ) : (
                    <div className="text-slate-300">لا توجد صورة</div>
                  )}
                  <Button
                    size="icon"
                    variant="ghost"
                    className="absolute top-2 left-2 bg-white/50 backdrop-blur-md rounded-full text-foreground hover:bg-white hover:text-red-500 h-9 w-9"
                    onClick={(e) => e.preventDefault()}
                  >
                    <Heart className="h-5 w-5" />
                  </Button>
                </Link>
                <CardContent className="p-4 flex-1">
                  <Link href={`/parts/${product.id}`}>
                    <h3 className="font-bold text-sm h-10 line-clamp-2 leading-snug mb-2 hover:text-primary transition-colors">{product.name_ar}</h3>
                  </Link>
                  {(product.customer_description_ar || product.customer_description) && (
                    <p className="text-[11px] text-slate-500 line-clamp-2 mb-2">
                      {product.customer_description_ar || product.customer_description}
                    </p>
                  )}
                  {Array.isArray(product.tags) && product.tags.length > 0 && (
                    <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                      {product.tags.slice(0, 2).map((tag, idx) => (
                        <span key={`${product.id}-tag-${idx}`} className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-slate-400 font-medium">{product.category_name}</div>
                    <div>
                      {product.compare_price && Number(product.compare_price) > Number(product.base_price) && (
                        <span className="text-[11px] text-slate-400 line-through ml-1">{product.compare_price}</span>
                      )}
                      <span className="text-lg font-bold text-secondary">{product.base_price}</span>
                      <span className="text-[10px] text-muted-foreground mr-1">{currencyLabel}</span>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="p-4 pt-0">
                  <Button
                    onClick={() => addToCart(product)}
                    className="w-full rounded-2xl bg-slate-900 hover:bg-primary transition-colors flex gap-2 h-11 text-xs"
                  >
                    <ShoppingCart className="h-4 w-4" />
                    أضف للسلة
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={isCheckoutOpen} onOpenChange={setIsCheckoutOpen}>
        <DialogTrigger asChild>
          <div className="fixed bottom-6 left-6 z-50">
            <Button className="h-16 w-16 rounded-full shadow-2xl bg-secondary hover:bg-secondary/90 flex flex-col items-center justify-center p-0 scale-110 transition-transform active:scale-100">
              <div className="relative">
                <ShoppingCart className="h-7 w-7 text-white" />
                {cart && cart.item_count > 0 && (
                  <span className="absolute -top-1 -right-1 bg-primary text-[10px] text-white font-bold h-5 w-5 rounded-full flex items-center justify-center border-2 border-secondary">
                    {cart.item_count}
                  </span>
                )}
              </div>
            </Button>
          </div>
        </DialogTrigger>
        <DialogContent className="max-w-2xl rounded-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold font-headline flex items-center gap-2">
              <PackageCheck className="h-6 w-6 text-primary" />
              سلة المشتريات وإتمام الطلب
            </DialogTitle>
          </DialogHeader>

          {!cart || cart.lines.length === 0 ? (
            <div className="py-12 text-center space-y-4">
              <p className="text-slate-500 font-medium">سلتك فارغة حالياً</p>
            </div>
          ) : (
            <form onSubmit={handlePlaceOrder} className="space-y-6">
              <div className="space-y-3">
                {cart.lines.map((item) => (
                  <div key={item.id} className="flex items-center gap-4 bg-slate-50 p-3 rounded-2xl border">
                    <div className="flex-1 min-w-0">
                      <h5 className="font-bold text-sm truncate">{item.variant_name}</h5>
                      <p className="text-xs text-muted-foreground">{item.unit_price} {currencyLabel} × {item.quantity}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-500 hover:bg-red-50"
                      onClick={() => removeFromCart(item.id)}
                      type="button"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>الاسم الكامل</Label>
                    <Input
                      placeholder="أدخل اسمك"
                      required
                      value={address.name}
                      onChange={e => setAddress({ ...address, name: e.target.value })}
                      className="rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>رقم الجوال</Label>
                    <Input
                      placeholder="05xxxxxxxx"
                      required
                      value={address.phone}
                      onChange={e => setAddress({ ...address, phone: e.target.value })}
                      className="rounded-xl text-left"
                      dir="ltr"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>المدينة</Label>
                  <Input
                    placeholder={cityPlaceholder}
                    required
                    value={address.city}
                    onChange={e => setAddress({ ...address, city: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
              </div>

              <div className="rounded-3xl border border-blue-100 bg-blue-50/60 p-4 space-y-3">
                <div>
                  <Label>طريقة الدفع</Label>
                  <p className="text-xs text-slate-500 mt-1">تظهر الطرق المفعلة لهذه الدولة من لوحة الإدارة.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {enabledPaymentMethods.map(({ value, label, hint, Icon }) => {
                    const active = paymentMethod === value;
                    return (
                      <button
                        type="button"
                        key={value}
                        onClick={() => setPaymentMethod(value)}
                        className={`rounded-2xl border p-3 text-right transition-all ${
                          active
                            ? 'border-primary bg-white shadow-sm ring-2 ring-primary/10'
                            : 'border-blue-100 bg-white/70 hover:border-primary/40'
                        }`}
                      >
                        <div className="flex items-center gap-2 font-black text-slate-800">
                          <Icon className={`h-4 w-4 ${active ? 'text-primary' : 'text-slate-400'}`} />
                          {label}
                        </div>
                        <p className="mt-1 text-[11px] leading-5 text-slate-500">{hint}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="rounded-3xl border border-orange-100 bg-orange-50/70 p-4 space-y-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <Label>كود الخصم</Label>
                    <p className="text-xs text-slate-500 mt-1">القسائم تُدار من لوحة التسويق والولاء في الإدارة.</p>
                  </div>
                  {couponPreview?.discount_amount && (
                    <Badge className="rounded-full bg-emerald-100 text-emerald-700">
                      خصم {couponPreview.discount_amount}
                    </Badge>
                  )}
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="WELCOME20"
                    value={couponCode}
                    onChange={e => {
                      setCouponCode(e.target.value.toUpperCase());
                      setCouponPreview(null);
                      setCouponError('');
                    }}
                    className="rounded-xl text-left"
                    dir="ltr"
                  />
                  <Button type="button" variant="outline" onClick={handleValidateCoupon} disabled={couponChecking || !couponCode.trim()} className="rounded-xl">
                    {couponChecking ? <Loader2 className="w-4 h-4 animate-spin" /> : 'تحقق'}
                  </Button>
                </div>
                {couponError && <p className="text-xs font-bold text-red-600">{couponError}</p>}
                {couponPreview?.valid && (
                  <p className="text-xs font-bold text-emerald-700">
                    {couponPreview.coupon?.title_ar || couponPreview.coupon?.title || couponCode} جاهزة للتطبيق عند تأكيد الطلب.
                  </p>
                )}
              </div>

              <div className="bg-slate-900 text-white p-6 rounded-3xl">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-xl font-bold">الإجمالي النهائي:</span>
                  <span className="text-3xl font-bold text-primary">{checkoutTotal.toFixed(2)} {currencyLabel}</span>
                </div>
                {couponPreview?.discount_amount && (
                  <div className="flex justify-between text-sm text-emerald-300 mb-4">
                    <span>خصم القسيمة</span>
                    <span>-{couponPreview.discount_amount} {currencyLabel}</span>
                  </div>
                )}
                {paymentMethod !== 'cod' && (
                  <p className="mb-4 rounded-2xl bg-white/10 px-4 py-3 text-xs text-blue-100">
                    الدفع الإلكتروني سيبقى معلقاً ومحجوزاً في الطلب حتى يتم تفعيل بوابة الدفع.
                  </p>
                )}
                <Button disabled={submittingOrder} type="submit" className="w-full h-14 rounded-2xl bg-primary hover:bg-primary/90 text-lg font-bold">
                  {submittingOrder ? <Loader2 className="w-6 h-6 animate-spin" /> : 'تأكيد الطلب الآن'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
